<template>
  <div
    role="radiogroup"
    :aria-label="t('settings.radioGroupSelect') + settingTheme?.label"
    class="options flex flex-wrap mt-1 mb-6"
  >
    <template v-for="option in settingTheme?.options" :key="option.value">
      <input
        :id="settingTheme.name + '-' + option.value"
        type="radio"
        class="hide-input"
        :checked="option.value === model"
        :name="settingTheme.name"
        :value="option.value"
        @change="model = <UserSettingCSSAppearance>option.value"
        @focus="handleInputFocus($event, option.value)"
        @blur="showAccessibleTooltip = false"
      />
      <label
        :for="settingTheme.name + '-' + option.value"
        class="option-name rounded-selector relative p-2 mx-2 my-1"
        :class="[
          'rounded-selector--' + option.label.toLowerCase(),
          showOnHover || showOnFocus ? 'rounded-selector--tooltip' : null,
        ]"
        @mouseenter="handleShowTooltip('mouseenter', $event, option.value)"
        @mouseleave="showAccessibleTooltip = false"
      >
        <span class="visibility-hidden">{{ option.label }}</span>
        <div
          v-if="currentSelectedOption === option.value"
          role="tooltip"
          class="accessible-tooltip"
          aria-labelledby="accessibleTooltipTitle"
        >
          <span id="accessibleTooltipTitle">
            {{ t("settings.themeOptions." + option.label) }}
          </span>
        </div>
      </label>
    </template>
  </div>
</template>

<script setup lang="ts">
import { PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { UserSetting, UserSettingCSSAppearance } from "@/helpers/settings";
import { adjustTooltipPosition } from "@/helpers/helperFunctions";

const { t } = useI18n();
const showAccessibleTooltip = ref<boolean>(false);
const currentSelectedOption = ref<string | null>(null);

const model = defineModel<UserSettingCSSAppearance>();

const props = defineProps({
  settingTheme: {
    type: Object as PropType<UserSetting>,
    default: null,
  },
  showOnHover: {
    type: Boolean,
    default: true,
  },
  showOnFocus: {
    type: Boolean,
    default: true,
  },
});

const handleShowTooltip = (eventName, event, optionName) => {
  currentSelectedOption.value = optionName;
  if (eventName === "mouseenter" && !props.showOnHover) {
    showAccessibleTooltip.value = false;
    return;
  }
  if (eventName === "focus" && !props.showOnFocus) {
    showAccessibleTooltip.value = false;
    return;
  }
  showAccessibleTooltip.value = true;
  const element = event.target as HTMLElement;
  const container = element?.closest(".settings-dialog");
  if (!element) return;
  setTimeout(() => adjustTooltipPosition(element, 10, container), 0);
};

const handleInputFocus = (event, optionName) => {
  currentSelectedOption.value = optionName;
  if (!props.showOnFocus) {
    showAccessibleTooltip.value = false;
    return;
  }
  showAccessibleTooltip.value = true;
  const element = event?.target as HTMLElement;
  const container = element?.closest(".settings-dialog");
  const label = container?.querySelector(
    `label[for="${element.id}"]`
  ) as HTMLElement;
  if (!label) return;
  setTimeout(() => adjustTooltipPosition(label, 10, container), 0);
};
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;

.rounded-selector {
  height: 3rem;
  width: 3rem;
  border-radius: 50%;
  border: 3px solid;
  &--default {
    background-color: var(--DM__color-dark);
    border-color: var(--DM__color-dark);
    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      transform: rotate(90deg) translateX(50%);
      transform-origin: top;
      height: 50%;
      width: 100%;
      border-radius: 0 0 60px 60px;
      background-color: var(--DM__color-light);
    }
  }
  &--light {
    background-color: var(--DM__color-light);
    border-color: var(--DM__color-dark);
  }
  &--sepia {
    background-color: var(--DM__color-sepia);
    border-color: var(--DM__color-darkSepia);
  }
  &--night {
    background-color: var(--DM__color-grayDark);
    border-color: var(--DM__color-grayShadow);
  }
  &--tooltip {
    position: relative;
    .accessible-tooltip {
      position: absolute;
      background-color: var(--DM__theme-font-color);
      color: var(--DM__theme-fill-color);
      border: 1px solid rgba(158, 158, 158, 0.2);
      box-shadow: 0 4px 23px rgb(0 0 0 / 9%);
      padding: 5px 10px;
      border-radius: 4px;
      font-size: 1rem;
      font-weight: bold;
      white-space: nowrap;
      z-index: 1;
      display: none;
      opacity: 0;
      animation: fade-in 0.1s forwards;
    }
    &:hover .accessible-tooltip,
    &:focus .accessible-tooltip {
      display: block;
    }
  }
}
.options {
  .hide-input {
    opacity: 0;
    position: absolute;
    z-index: -1;
    &:focus ~ label .accessible-tooltip {
      display: block;
    }
  }

  .hide-input:checked + .option-name {
    border: 3px solid var(--DM__color-sliderColor);
  }

  .hide-input:focus + .option-name {
    border: $spacingS solid var(--DM__theme-border-color);
  }

  .hide-input:focus:not(:focus-visible) + .option-name {
    border: 3px solid var(--DM__color-sliderColor);
  }

  .option-name {
    transition: all 0.1s ease;
    cursor: pointer;

    &:hover {
      border-color: var(--DM__color-sliderColor);
    }
  }
}
</style>
